/**
 * Email verification page component
 * Allows users to verify their email with the code sent via email
 * Styled with the app's orange design theme (otter-orange-500)
 */

import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Loader2, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { verifyEmail, resendCode } from '@/services/authApi';
import type { AuthError } from '@/types/auth';

interface LocationState {
  username?: string;
  /** Signup simplification (2026-07-29): carried from register so the fallback can prefill. */
  email?: string;
  /** True when register resent a code for an existing unconfirmed account. */
  codeResent?: boolean;
}

/**
 * Signup simplification (2026-07-29): this page has TWO ways to identify the account.
 *
 * WHY: the parent no longer chooses a username — the server generates one. A generated
 * username, unlike a chosen one, can NEVER be retyped. It arrives here in router state,
 * and router state dies on a refresh, a new tab, another device, or the entirely ordinary
 * "let me go and open my email" detour.
 *
 * And there is no way back. Signing in with the email returns "Invalid credentials", NOT
 * "please verify", because the email alias is not active until the account is confirmed and
 * login.py maps unknown-user and wrong-password to the same 401. So without a second route
 * in, a parent who loses this tab has an account they can never reach.
 *
 * Hence:
 *   - FAST PATH — username in router state. Ask only for the code. No lookup, so it is
 *     also immune to lookup lag in the seconds right after signup.
 *   - FALLBACK — no username. Ask for the email; the backend resolves it to the account.
 *
 * Do not "simplify" this to email-only: parents already mid-signup hold SELF-CHOSEN
 * usernames that nothing can derive, and email-only would strand exactly them.
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Present only on the fast path. Never editable — the parent did not choose it and
  // cannot meaningfully correct it; if it is wrong, the email fallback is the way out.
  const stateUsername = state?.username || '';
  const [email, setEmail] = useState(state?.email || '');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  // Which identifier this page will send. The username wins when present because it needs
  // no lookup; otherwise the typed email carries it.
  const identifier = stateUsername ? { username: stateUsername } : { email: email.trim() };
  const hasIdentifier = Boolean(stateUsername || email.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasIdentifier) {
      setError('Please enter your email address');
      return;
    }

    if (!code || code.length < 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsLoading(true);

    try {
      await verifyEmail({ ...identifier, code });
      setIsVerified(true);
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Email verified successfully! Please log in.',
          },
        });
      }, 2000);
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    // 2026-07-29: gated on EITHER identifier, not on username alone. Previously this
    // checked `!username` and the button was disabled to match — which meant the parent
    // in fallback mode, whose code is the most likely to have expired, found Resend
    // greyed out. The person most in need of it was the one person who could not use it.
    if (!hasIdentifier) {
      setResendError('Please enter your email address first');
      return;
    }

    setIsResending(true);
    setResendSuccess(null);
    setResendError(null);

    try {
      await resendCode(identifier);
      setResendSuccess('Verification email sent. Please check your inbox.');
    } catch (err) {
      const authError = err as AuthError;
      setResendError(
        authError.message || 'Unable to resend verification email. Please try again in a moment.'
      );
    } finally {
      setIsResending(false);
    }
  };

  if (isVerified) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
        style={{ backgroundColor: '#fdf8f0' }}
      >
        <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
          <CardHeader className="space-y-1 text-center">
            <CheckCircle className="mx-auto h-12 w-12 text-otter-orange-500" />
            <CardTitle className="text-2xl font-bold text-otter-earth-800">
              Email Verified!
            </CardTitle>
            <CardDescription className="text-otter-earth-700">
              Your email has been verified successfully. Redirecting to login...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-3 md:px-4 py-8 md:py-12"
      style={{ backgroundColor: '#fdf8f0' }}
    >
      <Card className="w-full max-w-md bg-otter-paper rounded-[20px] shadow-md border-[#E7D8C8]">
        <CardHeader className="space-y-1 text-center">
          <Mail className="mx-auto h-12 w-12 text-otter-orange-500" />
          <CardTitle className="text-2xl font-bold text-otter-earth-800">
            Verify Your Email
          </CardTitle>
          <CardDescription className="text-otter-earth-700">
            Enter the verification code sent to your email
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* 2026-07-29: register resent an existing code rather than creating a second
                account. Said plainly so a parent who forgot they had started is not left
                wondering why the code in their inbox is not the one they expected. */}
            {state?.codeResent && (
              <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800 border border-blue-200">
                You already started signing up with this email — we've sent you a fresh code.
              </div>
            )}

            {/* THE FALLBACK. Shown only when the username did not survive the trip here
                (refresh, new tab, another device). The parent is asked for the one thing
                they always know; the backend resolves it to the account.
                On the fast path this renders nothing at all — the username is already held
                and there is nothing useful to show, since the parent never chose it and
                could not correct it.
                type="email" is deliberate: it stops mobile autocapitalising, which matters
                because Cognito's alias matching is case-sensitive. */}
            {!stateUsername && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-otter-earth-800">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="h-11 md:h-10 border-[#E7D8C8]"
                />
                <p className="text-xs text-otter-earth-700">The address you signed up with.</p>
              </div>
            )}

            {/* Verification Code */}
            <div className="space-y-2">
              <Label htmlFor="code" className="text-otter-earth-800">
                Verification Code
              </Label>
              <Input
                id="code"
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                disabled={isLoading}
                maxLength={6}
                className="h-11 md:h-10 text-center text-lg tracking-widest border-[#E7D8C8]"
                autoComplete="one-time-code"
              />
              <p className="text-xs text-otter-earth-700">
                Check your email inbox (and spam folder) for the code
              </p>
            </div>

            {/* Error message */}
            {error && <p className="text-sm text-red-500">{error}</p>}
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              variant="warm"
              className="w-full rounded-[10px]"
              disabled={isLoading || !hasIdentifier || code.length < 6}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </Button>

            {/* Resend success message */}
            {resendSuccess && (
              <div className="w-full rounded-md bg-otter-paper p-3 text-sm text-otter-earth-700 border border-otter-orange-400">
                {resendSuccess}
              </div>
            )}

            {/* Resend error message */}
            {resendError && <p className="text-sm text-red-500">{resendError}</p>}

            <Button
              type="button"
              variant="warm"
              className="w-full rounded-[10px]"
              onClick={handleResend}
              // 2026-07-29: EITHER identifier, not username alone. See handleResend —
              // gating this on username stranded the fallback parent, who is precisely
              // the one whose code is most likely to have expired.
              disabled={isResending || !hasIdentifier}
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend Verification Email'
              )}
            </Button>

            <div className="text-center text-sm text-otter-earth-700 space-y-2">
              <p>
                Already verified?{' '}
                <Link
                  to="/login"
                  className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  Sign in
                </Link>
              </p>
              <p>
                Need a new account?{' '}
                <Link
                  to="/register"
                  className="text-otter-earth-700 hover:text-otter-orange-500 hover:underline"
                >
                  Register
                </Link>
              </p>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
